
import * as React from 'react';
import {
  IContextualMenuItem,
  ContextualMenuItemType
} from 'office-ui-fabric-react/lib/ContextualMenu';
import { mapObject, makeObject } from '../../utilities/objects';
import { ICommand, ICommandStateProps } from '../../models/commands/ICommand';
import {
  WithActionSet,
  IWithActionSetStateProps,
  renderAction,
  IWithActionSetProps,
  INITIAL_ACTION_STATE,
  IOnExecuteProps
} from '../withAction/WithAction';
import { IComponentDecorator, IDecoratedComponentType, IDecoratedProps } from '../../utilities/components/Decorators';
import { IRenderProps, renderChildrenWithProps } from '../../utilities/components/RenderProps';
import { mark } from '../../utilities/performance';
import { resetPerfLoggerFacet, resetPerfLoggerKeyFacet } from '../../facets/LoggerFacets';
import { connectToItemCache } from '../decorators/connectToItemCache';
import { IItemCacheDispatch } from '../../store/actions/ItemCacheActions';
import { readItem } from '../../operations/readItem';
import { withTelemetry, ITelemetryConsumerStateProps } from '../telemetry/TelemetryProvider';
import { Engagement } from '@ms/telemetry-manager/lib/events/Engagement.event';

type ICommandFilterProps = IRenderProps<ICommandSetStateProps> & ICommandSetStateProps & Pick<ICommandSetProps, 'onMenuItemsChanged'>;

class CommandFilter extends React.PureComponent<ICommandFilterProps> {
  public render(): JSX.Element | null {
    const {
      children,
      menuItems
    } = this.props;

    return renderChildrenWithProps(children, {
      menuItems
    });
  }

  public componentDidMount(): void {
    const {
      menuItems,
      onMenuItemsChanged
    } = this.props;

    if (onMenuItemsChanged) {
      onMenuItemsChanged(menuItems);
    }
  }

  public componentDidUpdate(previousProps: ICommandFilterProps): void {
    const {
      menuItems,
      onMenuItemsChanged
    } = this.props;

    if (menuItems !== previousProps.menuItems) {
      if (onMenuItemsChanged) {
        onMenuItemsChanged(menuItems);
      }
    }
  }

  public componentWillUnmount(): void {
    const {
      onMenuItemsChanged
    } = this.props;

    if (onMenuItemsChanged) {
      onMenuItemsChanged([]);
    }
  }
}

export { IOnExecuteProps };

/**
 * @public
 */
export interface ICommandSetStateProps {
  menuItems: ReadonlyArray<IContextualMenuItem & ICommandStateProps>;
}

/**
 * @internal
 */
export interface ICommandSetBaseProps extends IRenderProps<ICommandSetStateProps> {
  logCommandStart?: (command: ICommand) => void;
}

/**
 * @public
 */
export interface ICommandSetProps extends IRenderProps<ICommandSetStateProps> {
  commands: ReadonlyArray<ICommand> | undefined;
  onMenuItemsChanged?: (menuItems: ReadonlyArray<IContextualMenuItem & ICommandStateProps>) => void;
}

/**
 * Component which renders actions within commands and produces a
 * final, filtered set of menu items for the caller to use.
 * @public
 */
export class CommandSetBase extends React.PureComponent<ICommandSetProps & ICommandSetBaseProps> {
  constructor(props: ICommandSetProps & ICommandSetBaseProps) {
    super(props);

    this.state = {
      actions: {}
    };
  }

  public render(): JSX.Element {
    const {
      commands
    } = this.props;

    const actions: IWithActionSetProps['actions'] = {};

    if (commands) {
      const buildCommandElements = (currentCommands: ReadonlyArray<ICommand>, baseKey: string): void => {
        for (const command of currentCommands) {
          const {
            action
          } = command;

          const key = `${baseKey}-${command.key}`;

          if (action) {
            actions[key] = renderAction(action);
          }

          if (command.subMenuProps && command.subMenuProps.items) {
            buildCommandElements(command.subMenuProps.items, key);
          }
        }
      };

      buildCommandElements(commands, '');
    }

    return (
      <WithActionSet
        actions={ actions }
      >
        { (state: IWithActionSetStateProps) => {

          const menuItems: IContextualMenuItem[] = [];

          const {
            logCommandStart
          } = this.props;

          if (commands) {
            menuItems.push(...filterCommands(commands, state, '', logCommandStart));
          }

          return (
            <CommandFilter
              menuItems={ menuItems }
              onMenuItemsChanged={ this.props.onMenuItemsChanged }
            >
              { this.props.children }
            </CommandFilter>
          );
        } }
      </WithActionSet>
    );
  }
}

export const CommandSet: React.ComponentType<ICommandSetProps> = withTelemetry()(connectToItemCache(
  undefined,
  (dispatch: IItemCacheDispatch, props: ITelemetryConsumerStateProps): ICommandSetBaseProps => {
    const resetPerfLoggerKey = resetPerfLoggerKeyFacet.serialize({});

    const { resetPerfLogger } = dispatch(readItem({
      resetPerfLogger: resetPerfLoggerFacet
    }, resetPerfLoggerKey));

    const logCommandStart = (command: ICommand) => {
      const {
        engagement
      } = command;

      if (engagement) {
        props.telemetry(Engagement).logData({
          usageType: 'Click',
          ...engagement
        });
      }

      if (resetPerfLogger) {
        resetPerfLogger();
      }

      mark(`EUPL.${command.automationId}.click`);
    };

    return {
      logCommandStart
    };
  })(CommandSetBase));

/**
 * Filters a tree of contextual menu commands using current action state and
 * produces final versions of the commands with unavailable commands removed,
 * onClick callbacks wired up, and sub-menus filtered.
 */
function filterCommands(
  commands: ReadonlyArray<ICommand>,
  state: IWithActionSetStateProps,
  baseKey: string,
  logCommandStart?: (command: ICommand) => void
): ReadonlyArray<IContextualMenuItem & ICommandStateProps> {
  const items: (IContextualMenuItem & ICommandStateProps)[] = [];

  for (const command of commands) {
    const {
      action,
      automationId,
      postProcess,
      subMenuProps,
      ...menuItemProps
    } = command;

    const menuItem: IContextualMenuItem = {
      ...menuItemProps,
      ...(automationId ? { 'data-automationid': automationId } : {})
    };

    const key = `${baseKey}-${command.key}`;

    const actionState = state.actions[key] || INITIAL_ACTION_STATE;

    const isActionVisible = actionState.isAvailable || actionState.isToggled || command.isPermanent;
    const childItems = subMenuProps && (action && isActionVisible || !action) &&
      filterCommands(subMenuProps.items, state, key, logCommandStart);

    let item: IContextualMenuItem | undefined;

    if (action || subMenuProps) {
      if (isActionVisible || childItems && childItems.length > 0) {
        item = {
          ...menuItem,
          ...(isActionVisible ? {
            disabled: !actionState.isAvailable && !actionState.isToggled,
            canCheck: typeof actionState.isToggled === 'boolean',
            checked: actionState.isToggled,
            ...(actionState.isAvailable ? {
              onClick: (ev?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
                if (logCommandStart) {
                  logCommandStart(command);
                }

                // TODO Add QoS logging.
                if (ev) {
                  // Synthetic event will be reused for perf reasons unless
                  // we use event.persist(). https://fb.me/react-event-pooling
                  ev.persist();
                }

                actionState.execute({ event: ev }).catch((error: Error) => {
                  // Nowhere to put the error, so ignore it.
                });
              }
            } : {}),
            actionState: actionState
          } : {}),
          ...(childItems && childItems.length > 0 ? {
            ...(subMenuProps ? {
              subMenuProps: {
                ...subMenuProps,
                isBeakVisible: false,
                items: childItems as IContextualMenuItem[]
              },
              onClick: (ev?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
                if (logCommandStart) {
                  logCommandStart(command);
                }
              }
            } : {})
          } : {})
        };
      }
    } else {
      item = menuItem;
    }

    if (item) {
      items.push(postProcess ? postProcess(item) : item);
    }
  }

  return cleanUpDividers(items);
}

/**
 * Processes an array of contextual menu items to ensure that only single dividers are between
 * other items.
 */
function cleanUpDividers(items: ReadonlyArray<IContextualMenuItem>): ReadonlyArray<IContextualMenuItem> {
  let isDivider = true;

  const finalItems: IContextualMenuItem[] = [];

  for (const item of items) {
    if (item.itemType === ContextualMenuItemType.Divider) {
      if (isDivider) {
        continue;
      } else {
        isDivider = true;
      }
    } else {
      isDivider = false;
    }

    finalItems.push(item);
  }

  if (isDivider) {
    finalItems.pop();
  }

  return finalItems;
}

const NO_ITEMS: ReadonlyArray<ICommand> = [];

/**
 * Connects a component to zero or more 'command sets', which are groups of commands that evaluate into
 * menu items.
 * @internal
 */
export function connectToCommandSets<TInjectedProps, TOwnProps, K extends string>(
  mapPropsToCommands: (props: TOwnProps) => { [P in K]: ReadonlyArray<ICommand> | undefined; },
  mapMenuItemsToProps: (
    menuItems: { [P in K]: ReadonlyArray<IContextualMenuItem & ICommandStateProps> },
    props: TOwnProps
  ) => TInjectedProps
): IComponentDecorator<TInjectedProps, TOwnProps> {
  return <P extends TInjectedProps>(component: React.ComponentType<P>): IDecoratedComponentType<P, TInjectedProps, TOwnProps> => {
    const Component = component;

    type IProps = IDecoratedProps<P, TInjectedProps, TOwnProps>;

    class CommandSetConnector extends React.PureComponent<IProps> {
      public render(): JSX.Element {
        const mappedCommands = mapPropsToCommands(this.props as Readonly<TOwnProps>);

        const rootCommands: ICommand[] = [];

        for (const key in mappedCommands) {
          if (Object.prototype.hasOwnProperty.call(mappedCommands, key)) {
            const commands = mappedCommands[key];

            rootCommands.push({
              key: key,
              subMenuProps: {
                items: commands as ICommand[] || NO_ITEMS
              }
            });
          }
        }

        return (
          <CommandSet
            commands={ rootCommands }
          >
            { ({ menuItems }: ICommandSetStateProps) => {
              const mappedMenuItems = {
                ...mapObject(mappedCommands as { [R in K]: {} }, () => NO_ITEMS) as {},
                ...makeObject(
                  menuItems as IContextualMenuItem[],
                  (rootCommand: IContextualMenuItem) => {
                    return rootCommand && rootCommand.subMenuProps && rootCommand.subMenuProps.items || NO_ITEMS;
                  },
                  (rootCommand: IContextualMenuItem) => rootCommand.key as K) as {}
              } as { [R in K]: ReadonlyArray<IContextualMenuItem & ICommandStateProps>; };

              const props: P = {
                ...(this.props as {}),
                ...(mapMenuItemsToProps(mappedMenuItems, this.props as Readonly<TOwnProps>) as {})
              } as P;

              return (
                <Component { ...props } />
              );
            } }
          </CommandSet>
        );
      }
    }

    return CommandSetConnector;
  };
}
